import React, { useState, useEffect, useRef, useCallback } from "react";
import io from "socket.io-client";
import axios from "axios";
import Loader from "../../components/Live/Loader";
import { API } from "../../shared/api/Api";
import Tooltip from "../reusable/Tooltip";
import { useSelector } from "react-redux";
import { selectUserInformations } from "../../features/authSlice";
import ToggleSwitch from "../reusable/ToggleSwitch";

const ChatComponent = ({ room_uuid }) => {
  const [socket, setSocket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [chatEnabled, setChatEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const chatRef = useRef(null);
  const messageSet = useRef(new Set());
  const user = useSelector(selectUserInformations);

  const isDisabled = !chatEnabled && !user.instructor;

  const addMessage = useCallback((message) => {
    if (!messageSet.current.has(message.id)) {
      messageSet.current.add(message.id);
      setMessages((prev) => [...prev, message]);
    }
  }, []);

  // Scroll to the bottom
  const scrollToBottom = useCallback(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    const initializeSocket = () => {
      const newSocket = io(API.nestapi, {
        extraHeaders: { Authorization: `Bearer ${user.token}` },
        transports: ["polling"],
      });

      setSocket(newSocket);

      newSocket.on("connect", () => {
        newSocket.emit("joinRoom", { roomId: room_uuid });
      });

      newSocket.on("message", (data) => {
        addMessage(data);
      });

      newSocket.on("chat-enabled", (data) => {
        setChatEnabled(data.chat_enabled);
      });

      return newSocket;
    };

    const socketInstance = initializeSocket();
    return () => socketInstance.disconnect();
  }, [room_uuid, user.token, addMessage, chatEnabled]);

  useEffect(() => {
    fetchMessages(1);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    const handleScroll = () => {
      if (chatRef.current.scrollTop === 0 && hasMore && !isLoading) {
        fetchMessages(currentPage + 1);
      }
    };

    chatRef.current?.addEventListener("scroll", handleScroll);
    return () => chatRef.current?.removeEventListener("scroll", handleScroll);
  }, [currentPage, hasMore, isLoading]);

  const fetchMessages = async (page) => {
    try {
      setIsLoading(true);
      const { data } = await axios.get(
        `${API.Courses.app_courses_chat_allMessagesForRoom}/${room_uuid}`,
        {
          params: { page, limit: 8 },
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );

      if (data.status === 200) {
        const newMessages = data.data.items
          .reverse()
          .filter((message) => !messageSet.current.has(message.id));
        newMessages.forEach((message) => messageSet.current.add(message.id));
        setMessages((prev) => [...newMessages, ...prev]);
        setCurrentPage(page);
        setHasMore(!!data.data.next_page_url);
        if (page === 1) setTimeout(scrollToBottom, 100);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (isDisabled) return; // Prevent sending if disabled
    try {
      const { data } = await axios.post(
        `${API.Courses.app_courses_chat_sendMessage}/${room_uuid}`,
        {
          user_uuid: user.uuid,
          message: newMessage,
          type: "text",
        },
        {
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );

      if (data.status === 200) setNewMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const chatInput = (
    <>
      <input
        className={`flex-grow p-2 ml:p-3 border rounded-full text-xs sl:text-sm focus:outline-none shadow-sm ${isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
          }`}
        placeholder="Send a message..."
        value={newMessage}
        onChange={(e) => setNewMessage(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && sendMessage()}
        disabled={isDisabled}
      />
      <button
        onClick={sendMessage}
        disabled={isDisabled}
        className={`ml-2 sl:ml-3 px-3 sl:px-4 py-1.5 sl:py-2 rounded-full text-white text-xs sl:text-sm ${isDisabled
          ? "bg-gray-400 cursor-not-allowed"
          : "bg-blue-600 hover:bg-blue-700"
          }`}
      >
        Send
      </button>
    </>
  );

  return (
    <div className="flex flex-col h-full relative" style={{
      minHeight: '390px', maxHeight: '600px'
    }}>
      {/* Floating disabled message */}
      {isDisabled && (
        <div className="absolute top-0 left-0 right-0 z-10 bg-yellow-50 border-b border-yellow-200 px-4 py-2">
          <span className="text-xs text-yellow-800">
            Chat disabled for now. Wait for instructor to allow sending messages.
          </span>
        </div>
      )}

      {/* Chat area */}
      <div
        ref={chatRef}
        className={`flex-grow p-4 overflow-y-auto ${isDisabled ? 'pt-12' : ''}`}
        style={{
          height: 'calc(100% - 68px)'
        }}
      >
        {isLoading && <Loader />}
        {!hasMore && (
          <p className="text-center text-gray-500 text-sm">
            No more messages
          </p>
        )}
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex flex-col mb-4 ${message.user.uuid === user.uuid ? 'items-end' : 'items-start'
              }`}
          >
            <div className="flex items-center space-x-3">
              {message.user.uuid !== user.uuid && (
                <img
                  className="w-8 h-8 rounded-full object-cover"
                  src={message.user.image_url}
                  alt=""
                />
              )}
              <div
                className={`${message.user.uuid === user.uuid
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-900'
                  } p-3 rounded-lg shadow-md max-w-[75%]`}
              >
                <span className="text-sm">
                  {message.message}
                </span>
              </div>
              {message.user.uuid === user.uuid && (
                <img
                  className="w-8 h-8 rounded-full object-cover"
                  src={message.user.image_url}
                  alt=""
                />
              )}
            </div>
            <div className="mt-1">
              <span className="text-xs text-gray-500">
                {message.created_at}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Input area (fixed at bottom) */}
      <div className="sticky bottom-0 p-4 border-t rounded-xl bg-white z-20">
        <div className="flex items-center ll:max-w-[90%] ll:mx-auto">
          <input
            className={`flex-grow p-2 ll:p-3 border rounded-full text-xs sl:text-sm ll:text-base focus:outline-none shadow-sm ${isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
              }`}
            placeholder="Send a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            disabled={isDisabled}
          />
          <button
            onClick={sendMessage}
            disabled={isDisabled}
            className={`ml-2 sl:ml-3 ll:ml-4 px-3 sl:px-4 ll:px-5 py-1.5 sl:py-2 ll:py-2.5 rounded-full text-white text-xs sl:text-sm ll:text-base ${isDisabled
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
              }`}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatComponent;
